import { useCallback, useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { GraphFileItem, UserDocument } from "@/api/models"

import { downloadGraphFileApi } from "@/api/api"
import { graphDownloadErrorMessage } from "@/features/files/graph-download-error-message"
import { SettingsService } from "@/services/settings-service"

import { useAIParserSession } from "./use-ai-parser-session"
import { useUploadDocumentsMutation } from "./use-upload-documents-mutation"

export function useDirectFileUpload() {
  const { t } = useTranslation()
  const { currentSessionId } = useAIParserSession()
  const uploadDocumentsMutation = useUploadDocumentsMutation()
  const settingsService = SettingsService.getInstance()
  const [isFilePickerOpen, setIsFilePickerOpen] = useState(false)

  const handleLocalFilesSelected = useCallback(
    (files: File[]) => {
      uploadDocumentsMutation.mutate({
        sessionId: currentSessionId,
        files,
      })
    },
    [currentSessionId, uploadDocumentsMutation]
  )

  const handleGraphFileSelect = useCallback(
    async (item: GraphFileItem) => {
      try {
        const blob = await downloadGraphFileApi(item, settingsService.token)
        const mime = item.mimeType || blob.type || "application/octet-stream"
        const file = new File([blob], item.name, { type: mime })

        uploadDocumentsMutation.mutate({
          sessionId: currentSessionId,
          files: [file],
        })
      } catch (error) {
        toast.error(graphDownloadErrorMessage(error))
      }
    },
    [currentSessionId, settingsService.token, t, uploadDocumentsMutation]
  )

  const handleDocumentsSelected = useCallback(
    (documents: UserDocument[]) => {
      const files = documents.map((document) => {
        const extension = document.contentFormat === "markdown" ? ".md" : ".txt"
        const safeName = (document.title || "untitled-document").replace(/[/\\?%*:|"<>]/g, "-")
        return new File([document.content], `${safeName}${extension}`, { type: "text/plain" })
      })

      uploadDocumentsMutation.mutate({
        sessionId: currentSessionId,
        files,
      })
    },
    [currentSessionId, uploadDocumentsMutation]
  )

  const openFilePicker = useCallback(() => {
    setIsFilePickerOpen(true)
  }, [])

  const closeFilePicker = useCallback(() => {
    setIsFilePickerOpen(false)
  }, [])

  return {
    isFilePickerOpen,
    setIsFilePickerOpen,
    openFilePicker,
    closeFilePicker,
    handleLocalFilesSelected,
    handleGraphFileSelect,
    handleDocumentsSelected,
    isUploading: uploadDocumentsMutation.isPending,
  }
}
